import React from 'react';
import { useParams, Link } from 'react-router-dom';

//Components
import Spinner from '../components/Spinner';
import OrganizationInfo from '../components/OrganizationInfo';

import { useOrganization } from '../hooks/useOrganization'

const Organization = () => {
    
    const { organizationId } = useParams();
    const { error,
        notFound,
        organization,
        loading,
        subscriptionTypes
    } = useOrganization(organizationId);

    // An id that is neither "new" nor a plain number can never resolve
    // (e.g. a mistyped deep link) — show the not-found view straight away,
    // before mounting the tenant form.
    const idIsUnresolvable = organizationId !== "new" && !/^\d+$/.test(organizationId || "");

    if (notFound || idIsUnresolvable) return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="text-center">
                    <div className="text-gray-400 text-6xl mb-4">
                        <i className="fas fa-magnifying-glass"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Organization {organizationId} not found
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        The organization you are looking for does not exist or the link is invalid.
                    </p>
                    <Link to="/">
                        <button className="px-6 py-3 bg-grafokett-blue text-white rounded-lg hover:bg-grafokett-blue-dark transition-colors duration-200 font-medium">
                            <i className="fas fa-arrow-left mr-2"></i>
                            Back to Organizations
                        </button>
                    </Link>
                </div>
            </div>
        </div>
    )

    if (error) return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">
                        <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Something went wrong
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        There was an error loading the organization data.
                    </p>
                    <Link to="/">
                        <button className="px-6 py-3 bg-grafokett-blue text-white rounded-lg hover:bg-grafokett-blue-dark transition-colors duration-200 font-medium">
                            <i className="fas fa-home mr-2"></i>
                            Back to Home
                        </button>
                    </Link>
                </div>
            </div>
        </div>
    )

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header Section */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Link to="/">
                                <button className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 text-sm font-medium shadow-sm hover:shadow-md">
                                    <i className="fas fa-arrow-left mr-2"></i>
                                    Back to Organizations
                                </button>
                            </Link>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                    {organizationId === "new" ? "New Organization" : organization?.name || "Organization"}
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 mt-1">
                                    {organizationId === "new" ? "Create a new organization" : "Manage organization settings"}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            {organizationId !== "new" && organization && (
                                <div className="flex items-center space-x-2">
                                    <div className={`w-3 h-3 rounded-full ${
                                        organization.subscriptionStatus === "Active" 
                                            ? "bg-green-500" 
                                            : organization.subscriptionStatus === "Deleted"
                                            ? "bg-red-500"
                                            : "bg-gray-500"
                                    }`}></div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        {organization.subscriptionStatus}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <OrganizationInfo organization={organization} param={organizationId} subscriptionTypes={subscriptionTypes} />
                
                {loading && (
                    <div className="flex justify-center py-16">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 border border-gray-200 dark:border-gray-700">
                            <Spinner/>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default Organization;